import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../ui/utils";
import { Label } from "../ui/label";

import { Badge } from "../ui/badge";
import { Breadcrumb } from "../Breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { Plus, Edit, Trash2, Key, Search } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import API from "../api/api01";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

interface Customer {
  id: string;
  client_name: string;
  name: string;
  email: string;
  phone: string;
  status: "Active" | "Inactive";
  login_id: string;
  designation: string;
  permissions: string;
  group_id?: string | null;
  group_name?: string;
}

interface ClientOpt {
  id: string;
  name: string;
}

import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import { isValidEmail, isValidPhone, filterNumeric } from "../utils/validation";

interface ManageCustomerProps { }

export function ManageCustomer({ }: ManageCustomerProps) {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const [formData, setFormData] = useState({
    client_id: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    designation: "",
    login_id: "",
    password: "",
  });

  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<{ id: string, name: string }[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [permissions, setPermissions] = useState({
    view: true,
    insert: false,
    update: false,
    delete: false
  });
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      setCurrentUser(JSON.parse(savedUser));
    }
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, clientsRes] = await Promise.all([
        API.get("/superadmin/customers"),
        API.get("/superadmin/clients"),
      ]);

      setCustomers(customersRes.data);
      setClients(clientsRes.data);
      if (customersRes.data && customersRes.data.length > 0) {
        toast.success("Customers loaded successfully");
      } else {
        toast.error("No records found");
      }

      const groupsRes = await API.get("/superadmin/groups");
      setGroups(groupsRes.data.filter((g: any) => g.group_type === 'customer'));
    } catch (err) {
      toast.error("Failed to fetch data");
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const { data } = await API.get("/superadmin/module-permissions/manage-customer");
      setPermissions(data);
    } catch (error) {
      console.error("Failed to fetch permissions", error);
    }
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.login_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = async () => {
    if (!selectedCustomer) return;

    if (formData.email && !isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (formData.phone && !isValidPhone(formData.phone)) {
      toast.error("Please enter a valid mobile number (10-15 digits)");
      return;
    }

    setLoading(true);
    try {
      let permissions = undefined;
      let groupIdToSend = undefined;

      if (selectedGroupId === "no-group") {
        groupIdToSend = null;
      } else if (selectedGroupId !== "no-change" && selectedGroupId !== selectedCustomer.group_id) {
        groupIdToSend = selectedGroupId;
      }

      if (selectedGroupId && selectedGroupId !== "no-change" && selectedGroupId !== "no-group") {
        const permRes = await API.get(`/superadmin/group-permissions/${selectedGroupId}`);
        permissions = JSON.stringify(permRes.data);
      }

      const filteredData = Object.fromEntries(
        Object.entries(formData).filter(([k, v]) => v !== "" && k !== "client_id")
      );

      // Specialist handle for client_id since it might be empty string but shouldn't be sent if not changed
      if (formData.client_id) filteredData.client_id = formData.client_id;

      await API.put(`/superadmin/customers/${selectedCustomer.id}`, {
        ...filteredData,
        group_id: groupIdToSend,
        permissions
      });

      toast.success("Customer updated successfully");
      setIsEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to update customer");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;

    setLoading(true);
    try {
      await API.delete(`/superadmin/customers/${selectedCustomer.id}`);
      toast.success("Customer deleted successfully");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete customer");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (customer: Customer) => {
    const newStatus = customer.status === "Active" ? "Inactive" : "Active";
    try {
      await API.patch(`/superadmin/customers/${customer.id}/status`, { status: newStatus });
      toast.success(`Customer ${newStatus === "Active" ? "Activated" : "Deactivated"} Successfully`);
      fetchData(); // Refresh list
    } catch (err) {
      toast.error("Failed to update customer status");
    }
  };

  const handleUpdatePassword = () => {
    toast.info("Password Update API not implemented yet.");
    setIsPasswordDialogOpen(false);
  };

  const openEditDialog = (customer: Customer) => {
    setSelectedCustomer(customer);

    setFormData({
      client_id: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      designation: "",
      login_id: "",
      password: "",
    });

    // Try to find the client_id from the client_name if possible, but the API might need to return it
    const client = clients.find(c => c.name === customer.client_name);
    if (client) {
      setFormData(prev => ({ ...prev, client_id: client.id }));
    }

    setSelectedGroupId(customer.group_id || "no-change");
    setIsEditDialogOpen(true);
  };

  const openPasswordDialog = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsPasswordDialogOpen(true);
  };

  const openDeleteDialog = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* HEADER */}

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
                Manage Customer
              </h1>


              <Breadcrumb
                items={[
                  { label: "Manage Customer" },
                  { label: "All Customers" },
                ]}
                className="mt-2"
              />
            </div>


            <Button
              onClick={() => navigate(PATHS.ADD_CUSTOMER)}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:opacity-50 h-10 px-6 rounded-lg transition-all active:scale-95"
              disabled={!permissions.insert}
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="text-[13px] font-bold uppercase tracking-widest">Add Customer</span>
            </Button>

          </div>

          {/* SEARCH */}

          <Card className="bg-card border-slate-200 dark:border-slate-800 shadow-sm">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                <Input
                  placeholder="Search by name, email, client or login ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 transition-all focus:ring-2 focus:ring-primary/30 bg-background border-slate-200 dark:border-slate-800"
                />
              </div>
            </CardContent>
          </Card>

          {/* TABLE */}

          <Card className="bg-card border border-slate-200 dark:border-slate-800 shadow-sm">
            <CardHeader className="border-b border-slate-100 dark:border-slate-800/60 pb-3">
              <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Customers ({filteredCustomers.length})</CardTitle>
            </CardHeader>


            <CardContent>
              <Table>

                <TableHeader className="bg-muted/30">
                  <TableRow className="border-slate-200 dark:border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4 pl-6">Login ID</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Client Name</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Customer Name</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Email</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Designation</TableHead>
                    <TableHead className="text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4">Status</TableHead>
                    <TableHead className="text-right text-xs uppercase font-bold tracking-widest text-muted-foreground/70 py-4 pr-6">Actions</TableHead>
                  </TableRow>
                </TableHeader>


                <TableBody>
                  {filteredCustomers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-20 text-muted-foreground group">
                        <div className="flex flex-col items-center gap-3 animate-in fade-in zoom-in-95 duration-500">
                          <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center text-slate-400 group-hover:scale-110 transition-transform">
                            <Search className="h-8 w-8" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-lg font-semibold text-slate-900 dark:text-white">No customers found</p>
                            <p className="text-sm">Try adjusting your search or add a new customer to get started.</p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredCustomers.map((customer) => (

                    <TableRow
                      key={customer.id}
                      className="transition-colors hover:bg-background/50 border-white/5"
                    >

                      <TableCell className="text-[13px] font-bold text-blue-600 dark:text-blue-400 pl-6">{customer.login_id}</TableCell>

                      <TableCell className="text-[13px] font-medium text-slate-500 dark:text-slate-400">{customer.client_name}</TableCell>

                      <TableCell className="text-[13px] font-bold text-slate-900 dark:text-white">{customer.name}</TableCell>

                      <TableCell className="text-[13px] font-medium text-slate-500 dark:text-slate-400">{customer.email}</TableCell>

                      <TableCell className="text-[13px] font-medium text-slate-500 dark:text-slate-400">
                        <Badge variant="outline" className="text-[10px] font-bold uppercase tracking-widest border-slate-200 dark:border-white/10 bg-transparent px-2.5 py-0.5 whitespace-nowrap">
                          {customer.designation}
                        </Badge>
                      </TableCell>


                      {/* STATUS */}

                      <TableCell>

                        <div className="flex items-center gap-2 group">

                          <Switch
                            checked={customer.status === "Active"}
                            onCheckedChange={() =>
                              handleStatusToggle(customer)
                            }
                          />

                          <Badge
                            className={cn(
                              "text-[10px] font-bold uppercase tracking-widest px-2.5 py-0.5 border-none shadow-none",
                              customer.status === "Active"
                                ? "bg-emerald-500/10 text-emerald-600"
                                : "bg-slate-100 dark:bg-white/5 text-slate-500"
                            )}
                          >
                            {customer.status}
                          </Badge>


                        </div>

                      </TableCell>

                      {/* ACTIONS */}

                      <TableCell className="text-right">

                        <div className="flex justify-end gap-2">

                          {/* EDIT */}

                          <Tooltip>
                            <TooltipTrigger asChild>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(customer)}
                                className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 disabled:opacity-30"
                                disabled={!permissions.update}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>

                            </TooltipTrigger>

                            <TooltipContent>
                              Edit Customer
                            </TooltipContent>

                          </Tooltip>

                          {/* PASSWORD */}

                          <Tooltip>
                            <TooltipTrigger asChild>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  openPasswordDialog(customer)
                                }
                                className="hover:bg-purple-50 hover:text-purple-600 dark:hover:bg-purple-900/20 disabled:opacity-30"
                                disabled={!permissions.update}
                              >
                                <Key className="h-4 w-4" />
                              </Button>

                            </TooltipTrigger>

                            <TooltipContent>
                              Update Password
                            </TooltipContent>

                          </Tooltip>

                          {/* DELETE */}

                          <Tooltip>
                            <TooltipTrigger asChild>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  openDeleteDialog(customer)
                                }
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30"
                                disabled={!permissions.delete}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>

                            </TooltipTrigger>

                            <TooltipContent>
                              Delete Customer
                            </TooltipContent>

                          </Tooltip>

                        </div>

                      </TableCell>

                    </TableRow>

                  ))}
                </TableBody>

              </Table>
            </CardContent>
          </Card>

          {/* EDIT DIALOG */}

          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="bg-card border border-slate-200 dark:border-slate-800 shadow-xl max-h-[90vh] overflow-y-auto rounded-lg">

              <DialogHeader className="border-b border-slate-100 dark:border-slate-800/60 pb-4">
                <DialogTitle className="text-xl font-bold">Edit Customer</DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6">
                <div className="space-y-2 md:col-span-2 pb-2 border-b border-slate-100 dark:border-slate-800/60">
                  <Label>Update Group (Permissions)</Label>
                  <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                    <SelectTrigger className="border-slate-200 dark:border-slate-800">
                      <SelectValue placeholder="Select a Group to reset permissions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no-change">Don't Change Group</SelectItem>
                      <SelectItem value="no-group">None (Remove from Group)</SelectItem>
                      {groups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Selecting a group will override current customer permissions.</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Client</Label>
                  {currentUser?.role === "Client" ? (
                    <Input
                      value={
                        currentUser?.username === "lmsdemo" ? "lms demo" :
                          (currentUser?.name || selectedCustomer?.client_name || "Assigning Client...")
                      }
                      readOnly
                      className="w-full bg-background border-white/5 cursor-not-allowed"
                    />
                  ) : (
                    <Select
                      value={formData.client_id}
                      onValueChange={(val) => setFormData({ ...formData, client_id: val })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a Client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>


                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input
                    placeholder={selectedCustomer?.name}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder={selectedCustomer?.email}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    placeholder={selectedCustomer?.phone}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: filterNumeric(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input
                    placeholder={selectedCustomer?.designation}
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Login ID</Label>
                  <Input
                    placeholder={selectedCustomer?.login_id}
                    value={formData.login_id}
                    onChange={(e) => setFormData({ ...formData, login_id: e.target.value })}
                    className="border-slate-200 dark:border-slate-800"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Update Password (Leave blank to keep current)</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="border-slate-200 dark:border-slate-800"
                  />
                </div>
              </div>

              <DialogFooter className="border-t pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleEdit}
                  disabled={loading}
                  className="bg-primary text-white hover:bg-primary/90"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>

            </DialogContent>
          </Dialog>

          {/* PASSWORD DIALOG */}

          <Dialog
            open={isPasswordDialogOpen}
            onOpenChange={setIsPasswordDialogOpen}
          >

            <DialogContent className="bg-card border border-white/10 shadow-xl">

              <DialogHeader>
                <DialogTitle>Update Password</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">

                <p className="text-sm text-muted-foreground">
                  Updating password for:
                  <strong> {selectedCustomer?.name}</strong>
                </p>

                <div className="space-y-2">

                  <Label htmlFor="new-password">
                    New Password
                  </Label>

                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) =>
                      setNewPassword(e.target.value)
                    }
                  />

                </div>

              </div>

              <DialogFooter>

                <Button
                  variant="outline"
                  onClick={() => setIsPasswordDialogOpen(false)}
                >
                  Cancel
                </Button>

                <Button
                  onClick={handleUpdatePassword}
                  className="bg-primary text-white hover:bg-primary/90"
                >
                  Update Password
                </Button>

              </DialogFooter>

            </DialogContent>

          </Dialog>

          {/* DELETE CONFIRMATION */}

          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >

            <AlertDialogContent className="bg-card border border-white/10 shadow-2xl">

              <AlertDialogHeader>

                <AlertDialogTitle>
                  Are you sure?
                </AlertDialogTitle>

                <AlertDialogDescription>
                  This will permanently delete the customer
                  <strong> {selectedCustomer?.name}</strong>.
                  This action cannot be undone.
                </AlertDialogDescription>

              </AlertDialogHeader>

              <AlertDialogFooter>

                <AlertDialogCancel>
                  Cancel
                </AlertDialogCancel>

                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>

              </AlertDialogFooter>

            </AlertDialogContent>

          </AlertDialog>

        </div>
      </div>
    </TooltipProvider>
  );
}